import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import tonConnectService from '../services/tonConnect/tonConnectService';

const TonConnectContext = createContext({
  wallet: null,
  address: '',
  isConnected: false,
  isReady: false,
  error: null,
  connect: async () => null,
  disconnect: async () => {},
  sendTransaction: async () => ({ result: null, wallet: null }),
  getWalletAddress: () => '',
  service: tonConnectService,
});

export const TonConnectProvider = ({ children }) => {
  const [state, setState] = useState({
    wallet: null,
    address: '',
    isConnected: false,
    isReady: false,
    error: null,
  });

  useEffect(() => {
    let unsubscribeStatus;
    let cancelled = false;

    tonConnectService
      .init()
      .then(() => {
        if (cancelled) {
          return;
        }

        setState((prev) => ({
          ...prev,
          wallet: tonConnectService.getWallet(),
          address: tonConnectService.getWalletAddress(),
          isConnected: tonConnectService.isConnected(),
          isReady: true,
          error: null,
        }));

        unsubscribeStatus = tonConnectService.subscribeStatus(({ wallet, connected, address }) => {
          if (cancelled) {
            return;
          }
          setState((prev) => ({
            ...prev,
            wallet,
            address: address || tonConnectService.getWalletAddress(wallet),
            isConnected: connected,
            isReady: true,
          }));
        });
      })
      .catch((error) => {
        console.error('[TonConnectProvider] init failed:', error);
        if (cancelled) {
          return;
        }
        setState({
          wallet: null,
          address: '',
          isConnected: false,
          isReady: true,
          error,
        });
      });

    return () => {
      cancelled = true;
      if (typeof unsubscribeStatus === 'function') {
        unsubscribeStatus();
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      wallet: state.wallet,
      address: state.address,
      isConnected: state.isConnected,
      isReady: state.isReady,
      error: state.error,
      connect: (options) => tonConnectService.ensureConnected(options),
      disconnect: () => tonConnectService.disconnect(),
      sendTransaction: (tx, options) => tonConnectService.sendTransaction(tx, options),
      getWalletAddress: (wallet) => tonConnectService.getWalletAddress(wallet),
      service: tonConnectService,
    }),
    [state.wallet, state.address, state.isConnected, state.isReady, state.error],
  );

  return <TonConnectContext.Provider value={value}>{children}</TonConnectContext.Provider>;
};

export const useTonConnectContext = () => useContext(TonConnectContext);

